import { useState, type ReactNode } from 'react';
import { Num, Plural, T, useGT } from 'gt-react';
import type {
  WorkbookDefinition,
  WorkbookFreeformSheet,
  WorkbookResult,
} from '@erp/shared-types';
import {
  freeformFormulaText,
  freeformTableLayout,
  parseCellRange,
  storedFreeformFormula,
  tableItems,
  type WorkbookPlan,
} from '@erp/workbooks';
import { TrashIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SheetPreview } from '../SheetEditor';
import { SheetNameField, SheetPanelHeading } from '../SheetPanelHeading';
import { useColumnTypeOptions } from '../worksheetTypeLabels';
import { PivotValidations } from '../pivot/PivotValidations';
import { FreeformGrid } from './FreeformGrid';
import { withCell, withColumnType, withTableRange } from './freeformEdits';

export type FreeformPanelProps = {
  sheet: WorkbookFreeformSheet;
  definition: WorkbookDefinition;
  plan: WorkbookPlan;
  result?: WorkbookResult;
  stale: boolean;
  preview?: SheetPreview;
  needsRunHint: string;
  runMenu?: ReactNode;
  readOnly: boolean;
  change: (
    update: (sheet: WorkbookFreeformSheet) => WorkbookFreeformSheet,
  ) => void;
  /** Deletes the sheet (a step of the Workbook edit history, so undo restores it). */
  onDelete: () => void;
};

/**
 * A freeform sheet's tab: a formula bar and the grid, beside its settings —
 * its name, the range its Result table reads and each column's type, and its
 * Validations. Cells compute on Run; a Formula shows its text until then.
 */
export function FreeformPanel(props: FreeformPanelProps) {
  const gt = useGT();
  const { sheet, definition, result } = props;
  const [selected, select] = useState('A1');
  // With each table's current columns, so references by column name show as letters.
  const tables = tableItems(definition, props.plan.tables);
  const inputs = Object.fromEntries(
    Object.entries(sheet.cells).map(([address, input]) => [
      address,
      freeformFormulaText(input, tables),
    ]),
  );
  const diagnostics = props.plan.diagnostics.filter(
    (diagnostic) => diagnostic.location.itemId === sheet.id,
  );
  function commit(address: string, input: string) {
    const stored = storedFreeformFormula(input, tables, sheet.id);
    props.change((latest) => withCell(latest, address, stored));
  }
  const range = sheet.table ? parseCellRange(sheet.table.range) : null;
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <SheetPanelHeading
        sheet={sheet}
        definition={definition}
        diagnostics={diagnostics}
        preview={props.preview}
        runMenu={props.runMenu}
      />
      <div className="flex min-w-0 items-start gap-3">
        <section
          className="min-w-0 flex-1 space-y-2"
          aria-label={gt('Freeform sheet', {
            context: 'freeform grid section',
          })}
        >
          <FormulaBar
            key={`${selected}:${inputs[selected] ?? ''}`}
            address={selected}
            input={inputs[selected] ?? ''}
            readOnly={props.readOnly}
            onCommit={(input) => commit(selected, input)}
          />
          <FreeformGrid
            inputs={inputs}
            values={result?.freeformCells}
            table={range && { start: range.start, end: range.end }}
            selected={selected}
            onSelect={select}
            onCommit={commit}
            readOnly={props.readOnly}
            stale={props.stale}
          />
          <FreeformResultLine
            result={result}
            stale={props.stale}
            needsRunHint={props.needsRunHint}
          />
        </section>
        <aside
          className="w-72 shrink-0 space-y-3 rounded-xl border bg-card p-3"
          aria-label={gt('Freeform sheet settings', {
            context: 'freeform settings panel',
          })}
        >
          <SheetNameField
            name={sheet.name}
            readOnly={props.readOnly}
            rename={(name) => props.change((latest) => ({ ...latest, name }))}
          />
          <FreeformTableSettings
            key={sheet.table?.range ?? ''}
            sheet={sheet}
            readOnly={props.readOnly}
            change={props.change}
          />
          <PivotValidations
            sheet={sheet}
            result={result}
            readOnly={props.readOnly}
            change={(update) => props.change(update)}
          />
          {!props.readOnly && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive"
              onClick={props.onDelete}
            >
              <TrashIcon />
              <T context="freeform delete sheet">Delete sheet</T>
            </Button>
          )}
        </aside>
      </div>
    </div>
  );
}

/** The selected cell's address and what was typed into it, edited as in a spreadsheet's formula bar. */
function FormulaBar({
  address,
  input,
  readOnly,
  onCommit,
}: {
  address: string;
  input: string;
  readOnly: boolean;
  onCommit: (input: string) => void;
}) {
  const gt = useGT();
  const [draft, setDraft] = useState(input);
  return (
    <div className="flex items-center gap-2">
      <span className="w-14 shrink-0 rounded-md border px-2 py-1 text-center font-mono text-xs">
        {address}
      </span>
      <Input
        className="h-8 font-mono text-sm"
        aria-label={gt('Contents of cell {address}', {
          context: 'freeform formula bar',
          address,
        })}
        placeholder={gt("Type a value, or a formula such as ='Prices'!B3", {
          context: 'freeform formula bar placeholder',
        })}
        value={draft}
        disabled={readOnly}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          // Blur commits, once; Enter confirming an IME candidate doesn't.
          if (event.key === 'Enter' && !event.nativeEvent.isComposing)
            event.currentTarget.blur();
          if (event.key === 'Escape') setDraft(input);
        }}
        onBlur={() => {
          if (draft !== input) onCommit(draft);
        }}
      />
    </div>
  );
}

/** The range the Result table reads and each column's Declared type, named by its header cell. */
function FreeformTableSettings({
  sheet,
  readOnly,
  change,
}: {
  sheet: WorkbookFreeformSheet;
  readOnly: boolean;
  change: FreeformPanelProps['change'];
}) {
  const gt = useGT();
  const types = useColumnTypeOptions();
  const [draft, setDraft] = useState(sheet.table?.range ?? '');
  const [invalid, setInvalid] = useState(false);
  const layout = freeformTableLayout(sheet);
  function apply() {
    if (draft.trim() === (sheet.table?.range ?? '')) return;
    const next = withTableRange(sheet, draft);
    setInvalid(next === null);
    if (next) change((latest) => withTableRange(latest, draft) ?? latest);
  }
  return (
    <section className="space-y-2">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <T context="freeform result table range">Result table</T>
        <Input
          className="h-8 font-mono text-sm"
          placeholder={gt('A1:C20', {
            context: 'freeform result table range placeholder',
          })}
          value={draft}
          disabled={readOnly}
          aria-invalid={invalid}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') apply();
          }}
          onBlur={apply}
        />
        <span>
          {invalid ? (
            <T context="freeform result table range error">
              Use a range such as A1:C20.
            </T>
          ) : (
            <T context="freeform result table range help">
              Its first row names the columns. Reports, files and other sheets
              read this table.
            </T>
          )}
        </span>
      </label>
      {layout?.columns.map((column, index) => (
        <label
          key={column.header}
          className="flex items-center gap-2 text-xs text-muted-foreground"
        >
          <span className="min-w-0 flex-1 truncate" title={column.header}>
            {column.name ||
              gt('Column {column}', {
                context: 'freeform result column without a name',
                column: column.header,
              })}
          </span>
          <Select
            value={column.type}
            disabled={readOnly}
            onValueChange={(value) => {
              const type = types.find((option) => option.value === value);
              if (type)
                change((latest) => withColumnType(latest, index, type.value));
            }}
          >
            <SelectTrigger className="h-7 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {types.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      ))}
    </section>
  );
}

/** How many rows the Result table holds, or that the sheet needs a Run first. */
function FreeformResultLine({
  result,
  stale,
  needsRunHint,
}: {
  result?: WorkbookResult;
  stale: boolean;
  needsRunHint: string;
}) {
  if (!result)
    return <p className="text-xs text-muted-foreground">{needsRunHint}</p>;
  return (
    <p role="status" className="text-xs text-muted-foreground">
      {stale ? (
        <T context="freeform stale result">
          Cells changed since this run. Run again to recalculate.
        </T>
      ) : (
        <T context="freeform result rows">
          Result table: <Num>{result.rowCount}</Num>{' '}
          <Plural n={result.rowCount} one="row" other="rows" />
        </T>
      )}
    </p>
  );
}
